import React from 'react';
import { useHistory } from 'react-router-dom';
import Buttons2 from '../Settings/Buttons2';
import GenericCard from '../Shared/GenericCard';
import CommentBankDialog from '../Shared/Dialogs/commentBank';
import banks from '../../static/icons/banks.svg';
import { exportJsonFile } from '../../components2/markingCriteria';
import {
  createNewFeedbackBank,
  deleteSmartAnnotation,
  updateFeedbackBanks,
} from '../../service';
import { toast } from 'react-toastify';
import Toast from '../Toast';
import { buildClonePayload, buildMetadata } from '../../utils/subjects';
import { useUserStates } from '../../hooks/useUserStates';
import { useSubjects } from '../../hooks/useSubjects';

const CommentBankCard = ({
  commentBank,
  isSelected,
  onSelect,
  onEdit,
  showCheckbox = true,
  showActions = true,
  isSystem = false,
  cloneCommentBank = null,
  setCommentBankPreviewDialog,
  handleDownload,
  handleClone,
  handlePreview,
  handleOpenClick,
  disableNavigation = false,
}) => {
  const history = useHistory();
  const { states: userStates } = useUserStates();
  const { rawSubjects: subjectsList } = useSubjects(userStates);

  const handleNavigate = (item) => {
    if (!disableNavigation) {
      history.push(`/tools/commentbanks/${item.id}`);
    }
  };

  const handleCommentBankDownload = (item) => {
    if (handleDownload) {
      handleDownload(item);
    } else {
      const extractedCommentBank = {
        title: item.title,
        smartComments: item.smartComments,
      };
      exportJsonFile(extractedCommentBank, extractedCommentBank.title, 'cb');
    }
  };

  const handleCommentBankClone = () => {
    if (handleClone) {
      handleClone(commentBank);
    } else if (cloneCommentBank) {
      cloneCommentBank(commentBank);
    } else {
      const { smartComments } = commentBank;
      const newObject = buildClonePayload(commentBank, [], { smartComments });
      createNewFeedbackBank(newObject)
        .then(() => {
          toast(<Toast message={'feedback bank cloned'} />);
        })
        .catch((error) => {
          console.error('Error cloning feedback bank:', error);
        });
    }
  };

  const handleCommentBankPreview = () => {
    if (handlePreview) {
      handlePreview(commentBank);
    } else if (
      setCommentBankPreviewDialog &&
      commentBank?.smartComments?.length > 0
    ) {
      setCommentBankPreviewDialog(true);
    }
  };

  const handleCommentBankOpen = () => {
    if (handleOpenClick) {
      handleOpenClick(commentBank);
    } else if (commentBank && !commentBank.isSystem) {
      history.push(`/tools/commentbanks/${commentBank.id}`);
    }
  };

  const handleCommentBankDelete = (id) => {
    deleteSmartAnnotation(id)
      .then(() => {
        toast(<Toast message="Comment bank deleted successfully" />);
        window.location.reload();
      })
      .catch((error) => {
        toast(<Toast message="Failed to delete comment bank" />);
      });
  };

  // Helper to get subject code (handles both object and string formats)
  const getSubjectCode = (s) => {
    if (typeof s === 'object') return s.subjectCode || s.code || s;
    return s;
  };

  const handleCommentBankSettings = async (id, settings) => {
    try {
      const taskTypes = settings.taskTypes || [];
      const responseTypes = settings.responseTypes || [];
      const normalizedSubjects = (settings.subjects || [])
        .map((s) => getSubjectCode(s))
        .filter(Boolean);

      const metadata = buildMetadata(
        userStates,
        {
          years: settings.years || [],
          subjects: normalizedSubjects,
          taskTypes,
          responseTypes,
        },
        subjectsList
      );

      const updatePayload = {
        years: settings.years || [],
        subjects: normalizedSubjects,
        taskTypes,
        responseTypes,
        metadata,
      };

      await updateFeedbackBanks(updatePayload, id);
      toast(<Toast message="Settings saved successfully" />);
      window.location.reload();
    } catch (error) {
      console.error('Error updating comment bank settings:', error);
      toast(<Toast message="Failed to save settings" />);
    }
  };

  const badgeConfig = {
    icon: banks,
    label: 'Comment Bank',
  };

  const rightComponent = showActions ? (
    <Buttons2
      item={commentBank}
      itemType="Comment Bank"
      onDownload={handleCommentBankDownload}
      onDelete={handleCommentBankDelete}
      onClone={handleCommentBankClone}
      onPreview={handleCommentBankPreview}
      onSettings={handleCommentBankSettings}
    />
  ) : null;

  return (
    <>
      <GenericCard
        item={commentBank}
        type="commentBank"
        isSelected={isSelected}
        onSelect={onSelect}
        onEdit={onEdit}
        showCheckbox={showCheckbox}
        showActions={showActions}
        isSystem={commentBank?.isSystem}
        rightComponent={rightComponent}
        onNavigate={handleNavigate}
        onPreview={handleCommentBankPreview}
        badgeConfig={badgeConfig}
        disableNavigation={disableNavigation}
      />
    </>
  );
};

export default CommentBankCard;
